import { Injectable } from '@angular/core';
import { combineQueries } from '@datorama/akita';
import { AllDatasourcesQuery, DatasourceTableDictionary, DatasourceUIModel } from '@y42/datasource';
import { ErrorHandlerService } from '@y42/error-handling';
import { IntegrationQuery } from '@y42/integration';
import { SqlModelingQuery } from '@y42/sql-model';
import { UiModelingQuery } from '@y42/ui-model';
import { isNotNullish } from '@y42/util/nullish';
import { combineLatest, distinctUntilChanged, map, Observable, shareReplay } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class Y42AllDatasourcesQuery implements AllDatasourcesQuery {
  constructor(
    private readonly integrationQuery: IntegrationQuery,
    private readonly uiModelingQuery: UiModelingQuery,
    private readonly sqlModelingQuery: SqlModelingQuery,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  readonly loading$: Observable<boolean> = combineLatest([
    this.integrationQuery.loading$,
    this.uiModelingQuery.loading$,
    this.sqlModelingQuery.loading$,
  ]).pipe(
    map(
      ([integrationsLoading, uiModelingLoading, sqlModelingLoading]) =>
        integrationsLoading || uiModelingLoading || sqlModelingLoading,
    ),
    distinctUntilChanged(),
  );

  readonly listItems$ = combineQueries([
    this.integrationQuery.listItems$,
    this.uiModelingQuery.listItems$,
    this.sqlModelingQuery.listItems$,
  ]).pipe(map(([integrations, uiModels, sqlModels]) => [...integrations, ...uiModels, ...sqlModels]));

  readonly items$ = combineQueries([
    this.integrationQuery.items$,
    this.uiModelingQuery.items$,
    this.sqlModelingQuery.items$,
  ]).pipe(
    map(([integrations, uiModels, sqlModels]) => [...integrations, ...uiModels, ...sqlModels]),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly datasourceTableDictionary$ = this.items$.pipe(
    map((datasources: DatasourceUIModel[]) =>
      datasources.reduce<DatasourceTableDictionary>((acc, datasource) => {
        datasource.tables.forEach((t) => {
          acc[t.$path] = {
            table: t,
            datasource,
          };
        });
        return acc;
      }, {}),
    ),
  );

  selectAll() {
    return combineLatest([
      this.integrationQuery.selectAll(),
      this.uiModelingQuery.selectAll(),
      this.sqlModelingQuery.selectAll(),
    ]).pipe(map(([integrations, uiModels, sqlModels]) => [...integrations, ...uiModels, ...sqlModels]));
  }

  getEntity(datasourceId: string) {
    return (
      this.integrationQuery.getEntity(datasourceId) ||
      this.uiModelingQuery.getEntity(datasourceId) ||
      this.sqlModelingQuery.getEntity(datasourceId)
    );
  }

  getUIEntity(datasourceId: string) {
    return (
      this.integrationQuery.getUIEntity(datasourceId) ||
      this.uiModelingQuery.getUIEntity(datasourceId) ||
      this.sqlModelingQuery.getUIEntity(datasourceId)
    );
  }

  selectUIEntity(datasourceId: string) {
    return combineLatest([
      this.integrationQuery.selectUIEntity(datasourceId),
      this.uiModelingQuery.selectUIEntity(datasourceId),
      this.sqlModelingQuery.selectUIEntity(datasourceId),
    ]).pipe(
      map((entities) => {
        const matchingEntities = entities.filter(isNotNullish);
        if (matchingEntities.length > 1) {
          this.errorHandlerService.error('[AllDatasourcesQuery.selectUIEntity]: multiple entities have the same path', {
            context: {
              datasourceId,
              entities: matchingEntities.map((entity) => ({ name: entity.$name })),
            },
          });
        }
        return matchingEntities[0];
      }),
    );
  }

  getActiveUIModel() {
    return (
      this.integrationQuery.getActiveUIModel() ||
      this.uiModelingQuery.getActiveUIModel() ||
      this.sqlModelingQuery.getActiveUIModel()
    );
  }
}
